import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Avatar,
  Chip,
  Divider,
  Stack,
  useTheme,
  IconButton,
  Link,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  useMediaQuery,
  DialogTitle
} from '@mui/material';
import {
  Favorite,
  AccessTime,
  CheckCircle,
  Warning,
  HourglassEmpty,
  Link as LinkIcon,
  Comment as CommentIcon,
  Send,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../../../services/api';
import { formatDistanceToNow } from 'date-fns';

const PreviewCampaignDialog = ({ open, onClose, campaign }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  

  useEffect(() => {
    if (open && campaign.id) {
      fetchComments();
    }
  }, [open, campaign?.id, refresh]);

  if (!campaign) return null;

  const fetchComments = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/v1/api/campaigns/${campaign.id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      const response = await api.post(`/v1/api/campaigns/${campaign.id}/comments`, {
        message: newComment
      });
      setRefresh(!refresh);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      ACTIVE: <AccessTime color="primary" />,
      COMPLETED: <CheckCircle color="success" />,
      PENDING: <HourglassEmpty color="warning" />,
      EXPIRING: <Warning color="warning" />
    };
    return icons[status] || null;
  };


  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" fullScreen={isSmallScreen}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={campaign.imageUrl}
            variant="rounded"
            sx={{
              width: '100%',
              height: { xs: 200, md: 500 },
              borderRadius: '4px 4px 0 0'
            }}
          />
          <Chip
            label={campaign.campaignStatus}
            size="small"
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              fontWeight: 'bold',
              backgroundColor: theme.palette.background.paper,
              boxShadow: theme.shadows[2]
            }}
            icon={getStatusIcon(campaign.campaignStatus)}
          />
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', top: 8, right: 8, color: 'primary.contrastText', bgcolor: 'rgba(0,0,0,0.2)' }}
            size="small"
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            {campaign.title}
          </Typography>
          
          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <Chip 
              label={`$${campaign.amountRaised?.toLocaleString()} raised`}
              color="primary"
              variant="outlined"
            />
            <Chip 
              label={`$${campaign.goalAmount?.toLocaleString()} goal`} 
              variant="outlined"
            />
            <Chip
              label={`${campaign.donors} donors`}
              icon={<Favorite fontSize="small" />}
            />
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body1" paragraph sx={{ lineHeight: 1.6 }}>
            {campaign.description}
          </Typography>

          {campaign.daysLeft && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {campaign.daysLeft} days remaining
            </Typography>
          )}

          <Box sx={{ mt: 3, mb: 2, bgcolor: 'grey.200', borderRadius: 3, p: 1 }}>
            <Box sx={{ 
              width: `${Math.min(100, (campaign.amountRaised / campaign.goalAmount) * 100)}%`,
              height: 8,
              bgcolor: campaign.campaignStatus === 'COMPLETED' ? 'success.main' : 'primary.main',
              borderRadius: 3
            }} />
          </Box>

          {/* Comments Section */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CommentIcon sx={{ mr: 1 }} /> Comments ({comments.length})
            </Typography>
            
            {isLoading ? (
              <Typography>Loading comments...</Typography>
            ) : comments.length > 0 ? (
              <List sx={{ maxHeight: 300, overflow: 'auto', bgcolor: 'background.paper', borderRadius: 1 }}>
                {comments.map((comment) => (
                  <ListItem key={comment.id} alignItems="flex-start">
                    <ListItemAvatar>
                      <Avatar src={comment.user?.avatar || ''} alt={comment.user?.name} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <>
                          <Typography component="span" fontWeight="bold">
                            {comment.commenterName || 'Anonymous'}
                          </Typography>
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                          </Typography>
                        </>
                      }
                      secondary={
                        <Typography component="span" variant="body2">
                          {comment.message}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No comments yet. Be the first to comment!
              </Typography>
            )}
            
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                sx={{ mr: 1 }}
              />
              <IconButton 
                color="primary" 
                onClick={handleAddComment}
                disabled={!newComment.trim()}
              >
                <Send />
              </IconButton>
            </Box>
          </Box>

          <Stack 
            direction="row" 
            spacing={2} 
            sx={{ 
              mt: 3,
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            <Button
              variant="contained"
              color="primary"
              component={Link}
              href={`/fundraise-campaigns/${campaign.id}`}
              startIcon={<LinkIcon />}
            >
              View Full Campaign
            </Button>
            
            <Button 
              variant="outlined" 
              onClick={onClose}
              sx={{ ml: 'auto' }}
            >
              Close Preview
            </Button>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewCampaignDialog;